import { addOnDataMap } from '@apps-billing-shared/definitions';
import { getTranslations } from '@/lib/i18next';
import downgradeAnalytics from '@apps-billing-shared/assets/downgrade-analytics.jpg';
import downgradeHr from '@apps-billing-shared/assets/downgrade-hr.png';
import downgradePayroll from '@apps-billing-shared/assets/downgrade-payroll.jpg';
import downgradeMessaging from '@apps-billing-shared/assets/downgrade-messaging.jpg';

export const SUBSCRIPTION = {
  FREE: 0,
  PAID1: 1,
  PRO_TRIAL: 2,
  PREMIUM: 1,
  FLEXI: 3,
  MAXI: 4,
  SCHEDULING_ONLY: 5,
  TIMESHEET_ONLY: 6,
  ENTERPRISE: 10,
  PREMIUM_YEARLY: 11,
  MOBILE_ONLY: 20,
  FREEMIUM: 30,

  // New plans
  GOOD: 40,
  GOOD_ANNUAL: 41,
  GOOD_ANNUAL_PAID_MONTHLY: 43,
  BETTER: 50,
  BETTER_ANNUAL: 51,
  BETTER_ANNUAL_PAID_MONTHLY: 53,
  BEST: 60,
  BEST_ANNUAL: 61,
  BEST_ANNUAL_PAID_MONTHLY: 63,
} as const;

export type SubscriptionPlan = number;

export const CHANNEL = {
  CPQ: 'CPQ',
  APPSTORE_GOOGLE: 'APPSTORE_GOOGLE',
  APPSTORE_APPLE: 'APPSTORE_APPLE',
  RESELLER_XERO: 'RESELLER_XERO',
  EMBED_DRB: 'EMBED_DRB',
} as const;

export const EDIT_COMPANY_TABS = {
  GENERAL: 'edit-org-general',
  BILLING: 'edit-org-billing',
  PLANS: 'edit-org-plans',
  STATEMENT: 'edit-org-statement',
} as const;

export const FREE_PLANS = [
  SUBSCRIPTION.FREE,
  SUBSCRIPTION.PRO_TRIAL,
  SUBSCRIPTION.FREEMIUM,
] as const;

export const SUBSCRIPTION_NAME: Record<SubscriptionPlan, string> = {
  [SUBSCRIPTION.FREE]: 'Inactive',
  [SUBSCRIPTION.PAID1]: 'Premium',
  [SUBSCRIPTION.PRO_TRIAL]: 'Free Trial',
  [SUBSCRIPTION.PREMIUM]: 'Premium',
  [SUBSCRIPTION.FLEXI]: 'Flexi Weekly',
  [SUBSCRIPTION.MAXI]: 'Ultimate',
  [SUBSCRIPTION.SCHEDULING_ONLY]: 'Scheduling',
  [SUBSCRIPTION.TIMESHEET_ONLY]: 'Time & Attendance',
  [SUBSCRIPTION.ENTERPRISE]: 'Enterprise',
  [SUBSCRIPTION.PREMIUM_YEARLY]: 'Premium',
  [SUBSCRIPTION.MOBILE_ONLY]: 'Mobile Only',
  [SUBSCRIPTION.FREEMIUM]: 'Starter',

  // New plans
  [SUBSCRIPTION.GOOD]: 'Lite',
  [SUBSCRIPTION.GOOD_ANNUAL]: 'Lite Annual',
  [SUBSCRIPTION.GOOD_ANNUAL_PAID_MONTHLY]: 'Lite Annual Billed Monthly',
  [SUBSCRIPTION.BETTER]: 'Core',
  [SUBSCRIPTION.BETTER_ANNUAL]: 'Core Annual',
  [SUBSCRIPTION.BETTER_ANNUAL_PAID_MONTHLY]: 'Core Annual Billed Monthly',
  [SUBSCRIPTION.BEST]: 'Pro',
  [SUBSCRIPTION.BEST_ANNUAL]: 'Pro Annual',
  [SUBSCRIPTION.BEST_ANNUAL_PAID_MONTHLY]: 'Pro Annual Billed Monthly',
};

export const ADD_ONS = Object.entries(addOnDataMap).map(([addOnId, addOn]) => {
  const {
    name: title,
    nameShort: titleShort,
    imageSource,
    translations,
    featureFlag,
    featureFlagPreSubscribe,
    parentAddOn = '',
    equivalentPaymentFrequencyPlans = {},
  } = addOn;

  return {
    addOnId,
    title,
    titleShort,
    imageSource,
    description: translations?.description ?? '',
    features: translations?.features ?? [],
    featureFlag,
    featureFlagPreSubscribe,
    parentAddOn,
    equivalentPaymentFrequencyPlans,
  };
});

export const ADD_ON_STATUS = {
  ACTIVE: 'ACTIVE',
  CANCELLED: 'CANCELLED',
  INELIGIBLE: 'INELIGIBLE',
  INACTIVE: 'INACTIVE',
} as const;

export const ADD_ON_STATUS_ACTION = {
  SUBSCRIBE: 'subscribe',
  CANCEL: 'cancel',
} as const;

export const VERIFY_MOBILE_WORKFLOW = {
  UPDATE_MOBILE_WORKFLOW: 'UPDATE_MOBILE_WORKFLOW',
  VERIFY_MOBILE_WORKFLOW: 'VERIFY_MOBILE_WORKFLOW',
} as const;

export const addOns = {
  HR_ADD_ON_ID: 'hr-suite-standard',
  HR_ANNUAL_ADD_ON_ID: 'hr-suite-annual',
  HR_ANNUAL_PAID_MONTHLY_ADD_ON_ID: 'hr-suite-annual-paid-monthly',
  PAYROLL_ADD_ON_ID: 'payroll-standard',
  PAYROLL_ANNUAL_ADD_ON_ID: 'payroll-annual',
  PAYROLL_ANNUAL_PAID_MONTHLY_ADD_ON_ID: 'payroll-annual-paid-monthly',
  ADVANCED_REPORTING_ADD_ON_ID: 'reporting-standard',
  ADVANCED_REPORTING_ANNUAL_ADD_ON_ID: 'reporting-annual',
  ADVANCED_REPORTING_ANNUAL_PAID_MONTHLY_ADD_ON_ID:
    'reporting-annual-paid-monthly',
  MESSAGING_ADD_ON_ID: 'messaging-standard',
  MESSAGING_ANNUAL_ADD_ON_ID: 'messaging-annual',
  MESSAGING_ANNUAL_PAID_MONTHLY_ADD_ON_ID: 'messaging-annual-paid-monthly',
} as const;

export type AddOnId = (typeof addOns)[keyof typeof addOns];

export interface AddOnOptionalService {
  key: string;
  countries: readonly string[];
}

export const addOnOptionalServices: Partial<
  Record<AddOnId, readonly AddOnOptionalService[]>
> = {
  [addOns.PAYROLL_ADD_ON_ID]: [
    { key: '401k-integration', countries: ['US'] },
  ],
};

// Maps one-time-fee line-item identifiers (from the pricing API) to
// translation keys in the `once:checkout/summary` translation file.
export const ONE_TIME_FEE_LABEL_KEYS: Record<string, string> = {
  aca: 'one-time-fee-aca',
  standard_bundle: 'one-time-fee-standard-bundle',
  '401k_integration_setup_fee': 'one-time-fee-401k-integration-setup-fee',
};

// This is the map of editions with their equivalent plan type in Salesforce
export const editionToPlanTypeMap: Record<number, string> = {
  // Legacy plans
  0: 'Deputy Free',
  1: 'Deputy Premium',
  3: 'Deputy Flexi',
  5: 'Scheduling Only',
  6: 'Timesheet Only',
  10: 'Deputy Enterprise',
  11: 'Deputy Premium Yearly',

  // New plans
  40: 'Deputy Good',
  41: 'Deputy Good Yearly',
  43: 'Deputy Good APM',
  50: 'Deputy Better',
  51: 'Deputy Better Yearly',
  53: 'Deputy Better APM',
  60: 'Deputy Best',
  61: 'Deputy Best Yearly',
  63: 'Deputy Best APM',
};

/**
 * Visibility flags for conditional field display in the plan cancellation modal
 */
export const VISIBILITY_FLAGS = {
  SHOW_MISSING_FEATURES: 'showMissingFeatures',
  SHOW_ALTERNATIVE_SOLUTION: 'showAlternativeSolution',
  SHOW_ADDITIONAL_FEEDBACK: 'showAdditionalFeedback',
  SHOW_SOFTWARE_TYPE: 'showSoftwareType',
  SHOW_SOFTWARE_TYPE_SCHEDULING: 'showSoftwareTypeScheduling',
  SHOW_FUTURE_USE: 'showFutureUse',
  SHOW_CLOSEST_REASON: 'showClosestReason',
  SHOW_REASON_NOT_NEEDED: 'showReasonNotNeeded',
} as const;

export type VisibilityFlag =
  (typeof VISIBILITY_FLAGS)[keyof typeof VISIBILITY_FLAGS];

export const BUNDLE_DISSOLUTION_SCENARIO = {
  CHANGE_PLAN: 'change-plan',
  CHANGE_BILLING: 'change-billing',
  CANCEL_MONTHLY: 'cancel-monthly',
  // Annual upfront and annual-paid-monthly dissolve identically but need
  // different copy: upfront is prepaid, APM is not.
  CANCEL_ANNUAL: 'cancel-annual',
  CANCEL_ANNUAL_PAID_MONTHLY: 'cancel-annual-paid-monthly',
} as const;

export type BundleDissolutionScenario =
  (typeof BUNDLE_DISSOLUTION_SCENARIO)[keyof typeof BUNDLE_DISSOLUTION_SCENARIO];

export const addOnFeatures = {
  hr: {
    features: [
      { key: 'hire', icon: 'person-check', class: 'teal' },
      { key: 'hr-onboarding', icon: 'check-circle-solid', class: 'teal' },
      { key: 'document-management', icon: 'briefcase', class: 'teal' },
      { key: 'employee-engagement', icon: 'heart', class: 'teal' },
      { key: 'hr-reports', icon: 'graph', class: 'teal' },
    ],
    imageSrc: downgradeHr,
  },
  payroll: {
    features: [
      {
        key: 'simplified-payroll-processing',
        icon: 'calendar',
        class: 'blue',
      },
      { key: 'employee-payslips', icon: 'person', class: 'blue' },
      { key: 'payroll-reporting', icon: 'graph', class: 'blue' },
      {
        key: 'beam-super-clearing-house',
        icon: 'check-circle-solid',
        class: 'blue',
      },
      { key: 'multi-abn-support', icon: 'business', class: 'blue' },
    ],
    imageSrc: downgradePayroll,
  },
  messaging: {
    features: [
      {
        key: 'smart-location-based-groups',
        icon: 'location',
        class: 'lime',
      },
      {
        key: 'read-receipts-and-polls',
        icon: 'message-read',
        class: 'lime',
      },
      {
        key: 'access-level-based-permissions',
        icon: 'access-level',
        class: 'lime',
      },
      { key: 'pinned-messages', icon: 'pin', class: 'lime' },
    ],
    imageSrc: downgradeMessaging,
  },
  reporting: {
    features: [
      { key: 'library-of-reports', icon: 'star', class: 'violet' },
      { key: 'custom-reports', icon: 'share', class: 'violet' },
      { key: 'scheduled-report', icon: 'calendar', class: 'violet' },
      { key: 'metric-alerts', icon: 'notification', class: 'violet' },
    ],
    imageSrc: downgradeAnalytics,
  },
};

export const addOnCancellationReasons = [
  {
    id: 'COST_JUSTIFY',
    label: 'justify-cost',
    regrettable: true,
  },
  {
    id: 'MISSING_FEATURES',
    label: 'missing-features',
    regrettable: true,
  },
  {
    id: 'TOO_COMPLEX',
    label: 'too-complex',
    regrettable: true,
  },
  {
    id: 'SERVICE_OR_SUPPORT_ISSUES',
    label: 'service-or-support-issues',
    regrettable: true,
  },
  {
    id: 'FREE_VERSION_MEETS_NEEDS',
    label: 'free-version-meets-needs',
    regrettable: true,
  },
  {
    id: 'TIMING_ISNT_RIGHT',
    label: 'timing',
    regrettable: false,
  },
  {
    id: 'SOMETHING_ELSE',
    label: 'something-else',
    regrettable: false,
  },
];

export function generateAlternativeOptions(): Array<{
  value: string;
  label: string;
}> {
  const t = getTranslations({
    module: 'once',
    file: 'alternative-options',
  });
  return (t('options', { returnObjects: true }) as unknown as string[]).map(
    (option: string) => ({
      value: option,
      label: option,
    })
  );
}
